import React, { useState, useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Waypoint } from '../../types/walk'
import { Trash2, Edit2, Check, X, Plus } from 'lucide-react'
import { waypointIcons, WaypointType } from '../../config/waypointIcons'
import { parseCoordinates } from '../../utils/parseCoordinates'

interface WaypointManagerProps {
  initialWaypoints?: Waypoint[]
  onWaypointsChange: (waypoints: Waypoint[]) => void
}


const WaypointManager: React.FC<WaypointManagerProps> = ({ initialWaypoints = [], onWaypointsChange }) => {
  const [waypoints, setWaypoints] = useState<Waypoint[]>(initialWaypoints)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [addingWaypoint, setAddingWaypoint] = useState(false)
  const [newWaypointType, setNewWaypointType] = useState<WaypointType>('checkpoint')
  
  const mapRef = useRef<L.Map | null>(null)
  const markersRef = useRef<L.Marker[]>([])
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    // Create map centered on Western Australia
    const map = L.map(mapContainerRef.current, {
      zoomControl: true
    }).setView([-31.9505, 115.8605], 7)
    
    // Ensure map panes have lower z-index
    const mapPane = map.getPane('mapPane')
    if (mapPane) {
      mapPane.style.zIndex = '1'
    }
    
    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18
    }).addTo(map)

    // Add click handler for adding waypoints
    map.on('click', (e: L.LeafletMouseEvent) => {
      if (addingWaypoint) {
        const { lat, lng } = e.latlng
        addWaypoint({ lat, lng, type: newWaypointType })
        setAddingWaypoint(false)
      }
    })

    mapRef.current = map

    // If there are initial waypoints, fit map to them
    if (initialWaypoints.length > 0) {
      fitMapToWaypoints()
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  // Update markers when waypoints change
  useEffect(() => {
    updateMapMarkers()
  }, [waypoints])

  const fitMapToWaypoints = () => {
    if (!mapRef.current || waypoints.length === 0) return
    
    const bounds = L.latLngBounds(waypoints.map(wp => [wp.lat, wp.lng]))
    mapRef.current.fitBounds(bounds, { padding: [50, 50] })
  }

  const updateMapMarkers = () => {
    if (!mapRef.current) return

    // Remove existing markers
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []

    // Add new markers
    waypoints.forEach((waypoint, index) => {
      const wpType = (waypoint.type || 'checkpoint') as WaypointType
      const iconConfig = waypointIcons[wpType]
      
      const icon = L.divIcon({
        className: 'custom-waypoint-marker',
        html: iconConfig.mapMarkerHtml(index),
        iconSize: [32, 32],
        iconAnchor: [16, 32]
      })

      const marker = L.marker([waypoint.lat, waypoint.lng], { icon })
        .addTo(mapRef.current!)
        .bindPopup(waypoint.name || `${iconConfig.label} ${index + 1}`)
      
      markersRef.current.push(marker)
    })
  }

  const addWaypoint = (waypoint: Partial<Waypoint>) => {
    const newWaypoint: Waypoint = {
      ...waypoint,
      lat: waypoint.lat!,
      lng: waypoint.lng!,
      type: waypoint.type || 'checkpoint',
      order_index: waypoints.length
    }
    
    const updatedWaypoints = [...waypoints, newWaypoint]
    setWaypoints(updatedWaypoints)
    onWaypointsChange(updatedWaypoints)
  }

  const updateWaypoint = (index: number, updates: Partial<Waypoint>) => {
    const updatedWaypoints = waypoints.map((wp, i) => 
      i === index ? { ...wp, ...updates } : wp
    )
    setWaypoints(updatedWaypoints)
    onWaypointsChange(updatedWaypoints)
  }

  const removeWaypoint = (index: number) => {
    const updatedWaypoints = waypoints.filter((_, i) => i !== index)
      .map((wp, i) => ({ ...wp, order_index: i })) // Reindex
    setWaypoints(updatedWaypoints)
    onWaypointsChange(updatedWaypoints)
  }

  const moveWaypoint = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === waypoints.length - 1)
    ) return

    const newIndex = direction === 'up' ? index - 1 : index + 1
    const updatedWaypoints = [...waypoints]
    const temp = updatedWaypoints[index]
    updatedWaypoints[index] = updatedWaypoints[newIndex]
    updatedWaypoints[newIndex] = temp
    
    // Update order indices
    updatedWaypoints.forEach((wp, i) => {
      wp.order_index = i
    })
    
    setWaypoints(updatedWaypoints)
    onWaypointsChange(updatedWaypoints)
  }

  const handleSearch = async () => {
    if (!searchQuery.trim() || isSearching) return

    setIsSearching(true)
    setSearchResults([])

    // If the query is a coordinate pair or a Google Maps link, use it directly.
    const coord = parseCoordinates(searchQuery)
    if (coord) {
      setSearchResults([{
        lat: String(coord.lat),
        lon: String(coord.lng),
        display_name: `Pinned location, ${coord.lat.toFixed(5)}, ${coord.lng.toFixed(5)}`,
        __coord: true
      }])
      setIsSearching(false)
      return
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `format=json&q=${encodeURIComponent(searchQuery)}` +
        `&limit=8`,
        {
          headers: {
            'User-Agent': 'DayByDay/1.0'
          }
        }
      )

      if (!response.ok) throw new Error('Search failed')

      const results = await response.json()
      setSearchResults(results)
    } catch (error) {
      console.error('Search error:', error)
    } finally {
      setIsSearching(false)
    }
  }

  const handleSearchInput = (value: string) => {
    setSearchQuery(value)
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (value.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch()
      }, 500)
    } else {
      setSearchResults([])
    }
  }

  const handleSelectSearchResult = (result: any) => {
    const lat = parseFloat(result.lat)
    const lng = parseFloat(result.lon)
    
    addWaypoint({ 
      lat, 
      lng, 
      name: result.display_name.split(',')[0],
      type: newWaypointType
    })
    
    if (mapRef.current) {
      mapRef.current.setView([lat, lng], result.__coord ? 15 : 12)
    }

    setSearchResults([])
    setSearchQuery('')
  }

  const startEditingName = (index: number) => {
    setEditingIndex(index)
    setEditingName(waypoints[index].name || '')
  }

  const saveEditingName = () => {
    if (editingIndex !== null) {
      updateWaypoint(editingIndex, { name: editingName })
      setEditingIndex(null)
      setEditingName('')
    }
  }

  const cancelEditingName = () => {
    setEditingIndex(null)
    setEditingName('')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800">Waypoints</h3>
        <button
          onClick={() => fitMapToWaypoints()}
          className="text-sm text-blue-600 hover:text-blue-700"
          disabled={waypoints.length === 0}
        >
          Fit map to waypoints
        </button>
      </div>

      {/* Add waypoint controls */}
      <div className="bg-slate-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Add waypoint:</label>
          <select
            value={newWaypointType}
            onChange={(e) => setNewWaypointType(e.target.value as WaypointType)}
            className="px-3 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            {Object.entries(waypointIcons).map(([type, config]) => (
              <option key={type} value={type}>{config.label}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setAddingWaypoint(!addingWaypoint)}
            className={`flex-1 px-4 py-2 rounded-lg transition-colors duration-200 ${
              addingWaypoint 
                ? 'bg-blue-600 text-white' 
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {addingWaypoint ? 'Click on map to add' : 'Add by clicking map'}
          </button>
          
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search location, or paste coordinates / Google Maps link"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
            {isSearching && (
              <div className="absolute right-3 top-2.5">
                <svg className="animate-spin h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {searchResults.map((result, index) => (
              <button
                key={index}
                onClick={() => handleSelectSearchResult(result)}
                className="w-full text-left px-4 py-2 hover:bg-slate-50 transition-colors duration-200 text-sm"
              >
                <div className="font-medium text-slate-900">
                  {result.display_name.split(',')[0]}
                </div>
                <div className="text-xs text-slate-500">
                  {result.display_name.split(',').slice(1).join(',')}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Waypoints list */}
      {waypoints.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-700">Current waypoints:</h4>
          <div className="space-y-1">
            {waypoints.map((waypoint, index) => {
              const wpType = (waypoint.type || 'checkpoint') as WaypointType
              const iconConfig = waypointIcons[wpType]
              const IconComponent = iconConfig.icon
              
              return (
                <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-200">
                  <IconComponent className="w-5 h-5" style={{ color: iconConfig.color }} />
                  <span className="text-sm font-medium text-slate-500 w-8">{index + 1}</span>
                  
                  {editingIndex === index ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        autoFocus
                      />
                      <button onClick={saveEditingName} className="text-green-600 hover:text-green-700">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={cancelEditingName} className="text-red-600 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-slate-700">
                        {waypoint.name || `${iconConfig.label} ${index + 1}`}
                      </span>
                      
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startEditingName(index)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => moveWaypoint(index, 'up')}
                          disabled={index === 0}
                          className="text-slate-500 hover:text-slate-700 disabled:opacity-30"
                        >
                          ↑
                        </button>
                        
                        <button
                          onClick={() => moveWaypoint(index, 'down')}
                          disabled={index === waypoints.length - 1}
                          className="text-slate-500 hover:text-slate-700 disabled:opacity-30"
                        >
                          ↓
                        </button>
                        
                        <select
                          value={waypoint.type}
                          onChange={(e) => updateWaypoint(index, { type: e.target.value as WaypointType })}
                          className="px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        >
                          {Object.entries(waypointIcons).map(([type, config]) => (
                            <option key={type} value={type}>{config.label}</option>
                          ))}
                        </select>
                        
                        <button
                          onClick={() => removeWaypoint(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Map */}
      <div className="relative z-0">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Map View
        </label>
        <div 
          ref={mapContainerRef} 
          className="h-96 rounded-lg border border-slate-200 overflow-hidden relative z-0"
          style={{ zIndex: 0 }}
        />
      </div>
    </div>
  )
}

export default WaypointManager